/**
 * Chat scroll geometry
 *
 * Pin-to-top chats keep a trailing spacer after the last message so a new
 * turn can sit near the top while the reply streams. "Bottom" for those
 * chats is NOT raw scrollHeight (that lands in empty spacer air). It is
 * the scrollTop that puts the end of real content just above the sticky
 * composer.
 *
 * CRITICAL: never use the element's offset-top relative to offsetParent for
 * this. The spacer is nested under relative/sticky wrappers inside the
 * scroller, so that value is not relative to the scroll container and
 * undershoots into the void (scroll-to-bottom looks "done" while half the
 * screen is empty). Always measure via getBoundingClientRect + scrollTop.
 */

export const PIN_AWARE_BOTTOM_THRESHOLD_PX = 48

export function getElementOffsetInScrollContainer(
  el: HTMLElement,
  scrollEl: HTMLElement
): number {
  const elRect = el.getBoundingClientRect()
  const scrollRect = scrollEl.getBoundingClientRect()
  return elRect.top - scrollRect.top + scrollEl.scrollTop
}

/**
 * ScrollTop that places the usable bottom of the thread in view.
 *
 * - pinToTop + spacer: align spacer top with the top of the sticky composer
 *   (viewport bottom minus composerInset), so the last message sits flush
 *   above the input — not buried behind it, and not floating over empty air.
 * - otherwise: classic scrollHeight bottom.
 */
export function readPinAwareBottomTop(
  scrollEl: HTMLElement,
  spacer: HTMLElement | null | undefined,
  pinToTop: boolean,
  composerInsetPx = 0
): number {
  if (pinToTop && spacer) {
    const spacerTop = getElementOffsetInScrollContainer(spacer, scrollEl)
    const inset = Math.max(0, composerInsetPx)
    return Math.max(0, spacerTop - scrollEl.clientHeight + inset)
  }

  return Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight)
}

export function readDistanceFromBottom(
  scrollEl: HTMLElement,
  spacer: HTMLElement | null | undefined,
  pinToTop: boolean,
  composerInsetPx = 0
): number {
  const bottomTop = readPinAwareBottomTop(
    scrollEl,
    spacer,
    pinToTop,
    composerInsetPx
  )
  return Math.max(0, bottomTop - scrollEl.scrollTop)
}
